import React, { useState, useContext } from "react";
import { AuthContext } from "./context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import logo from "./logo_img.png";
import Navbar from "./Navbar";
import Footer from "./components/Footer";

// Add style for spotlight animation
const spotlightAnimationStyle = `
@keyframes spotlight {
  0% {
    opacity: 0;
    transform: translateX(-100%) skewX(-10deg);
  }
  20% {
    opacity: 0.6;
  }
  40% {
    opacity: 0.3;
  }
  100% {
    opacity: 0;
    transform: translateX(100%) skewX(-10deg);
  }
}

.animate-spotlight {
  animation: spotlight 10s linear infinite;
}
`;

function Pricing() {
  return (
    <div className="relative flex h-screen overflow-hidden justify-center">
      {/* Add style tag for animation */}
      <style>{spotlightAnimationStyle}</style>
      <div className="flex flex-col h-screen w-full px-4">
        <div className="navbar bg-transparent">
          <div className="navbar-start items-center">
            <Link to="/" className="items-center flex flex-row">
              <img src={logo} alt="Logo" className="w-1 sm:mx-auto" />
            </Link>
          </div>
        </div>

        <div className="flex flex-col h-full justify-items-center items-center bg-gradient-to-b from-slate-50 to-white overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
          {/* Main Content Container */}
          <div className="w-full max-w-7xl py-8 sm:py-12 px-4">
            {/* Pricing Header */}
            <div className="text-center mb-16">
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
                Choose Your Perfect Plan
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Start your SQL learning journey with our flexible pricing
                options designed to fit your needs
              </p>
            </div>

            {/* Pricing Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              <div className="flex items-stretch">
                <div className="relative transition-all duration-300 hover:translate-y-[-4px] w-full">
                  <div className="h-full min-h-[500px] rounded-3xl overflow-hidden bg-white border border-blue-200 shadow-md hover:shadow-xl transition-all duration-300 relative">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-8 py-6 border-b border-blue-100">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xl font-bold text-blue-900">
                          Monthly Essential
                        </h3>
                        <div className="rounded-full bg-white p-2 border border-blue-200">
                          <svg
                            className="h-5 w-5 text-blue-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                      </div>
                      <p className="text-blue-700 text-sm">
                        Perfect for students and beginners
                      </p>
                    </div>

                    {/* Body */}
                    <div className="px-8 py-6 pb-20">
                      <div className="mb-6">
                        <div className="flex items-baseline">
                          <span className="text-4xl font-bold text-gray-900">
                            $19
                          </span>
                          <span className="ml-2 text-gray-600">/month</span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          Billed monthly
                        </p>
                      </div>

                      <ul className="space-y-4">
                        {[
                          "SQL interview queries practice",
                          "Real world SQL problems",
                          "120+ SQL questions",
                          "Basic Support via messages",
                          "Billed monthly",
                        ].map((feature) => (
                          <li key={feature} className="flex items-center">
                            <div className="mr-3 text-blue-500">
                              <svg
                                className="h-5 w-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="absolute bottom-0 left-0 right-0 px-8 py-6">
                      <Link to="/payment">
                        <button className="w-full rounded-lg bg-blue-500 hover:bg-blue-600 px-6 py-3 text-white font-semibold transition-colors duration-200 shadow-sm">
                          Get Started with Monthly Plan
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-stretch">
                <div className="relative transition-all duration-300 hover:translate-y-[-4px] w-full">
                  {/* Best Value Badge */}
                  <div className="absolute -top-3 right-5 z-10">
                    <div className="bg-indigo-600 text-white text-xs font-bold uppercase py-1 px-3 rounded-full shadow-md">
                      Best Value
                    </div>
                  </div>

                  <div className="h-full min-h-[500px] rounded-3xl overflow-hidden bg-white border border-indigo-200 shadow-lg hover:shadow-xl transition-all duration-300 relative">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 px-8 py-6 border-b border-indigo-100">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xl font-bold text-indigo-900">
                          Yearly Essential
                        </h3>
                        <div className="rounded-full bg-white p-2 border border-indigo-200">
                          <svg
                            className="h-5 w-5 text-indigo-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                      </div>
                      <p className="text-indigo-700 text-sm">
                        Save $29 with annual billing
                      </p>
                    </div>

                    {/* Body */}
                    <div className="px-8 py-6">
                      <div className="mb-6">
                        <div className="flex items-baseline">
                          <span className="text-4xl font-bold text-gray-900">
                            $199
                          </span>
                          <span className="ml-2 text-gray-600">/year</span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          Billed annually
                        </p>
                      </div>

                      <ul className="space-y-4 mb-8">
                        {[
                          "SQL interview queries practice",
                          "Real world SQL problems",
                          "120+ SQL questions",
                          "Basic Support via messages",
                          "Billed annually",
                        ].map((feature) => (
                          <li key={feature} className="flex items-center">
                            <div className="mr-3 text-indigo-500">
                              <svg
                                className="h-5 w-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="absolute bottom-0 left-0 right-0 px-8 py-6">
                      <Link to="/payment">
                        <button className="w-full rounded-lg bg-indigo-600 hover:bg-indigo-700 px-6 py-3 text-white font-semibold transition-colors duration-200 shadow-sm">
                          Get Started with Yearly Plan
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-stretch">
                <div className="relative transition-all duration-300 hover:translate-y-[-4px] w-full">
                  <div className="h-full min-h-[520px] rounded-3xl overflow-hidden bg-white border border-purple-200 shadow-md hover:shadow-xl transition-all duration-300 relative">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-purple-50 to-purple-100 px-8 py-6 border-b border-purple-100">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xl font-bold text-purple-900">
                          Enterprise
                        </h3>
                        <div className="rounded-full bg-white p-2 border border-purple-200">
                          <svg
                            className="h-5 w-5 text-purple-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                            />
                          </svg>
                        </div>
                      </div>
                      <p className="text-purple-700 text-sm">
                        Custom solutions for organizations
                      </p>
                    </div>

                    {/* Body */}
                    <div className="px-8 py-6 pb-20">
                      <div className="mb-6">
                        <div className="flex items-baseline">
                          <span className="text-2xl font-bold text-gray-900">
                            Coming Soon
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          Custom pricing for teams
                        </p>
                      </div>

                      <ul className="space-y-4">
                        {[
                          "Custom branding",
                          "Dedicated support",
                          "Analytics dashboard",
                          "Custom features",
                        ].map((feature) => (
                          <li key={feature} className="flex items-center">
                            <div className="mr-3 text-purple-500">
                              <svg
                                className="h-5 w-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="absolute bottom-0 left-0 right-0 px-8 py-6">
                      <button className="w-full rounded-lg bg-purple-600 hover:bg-purple-700 px-6 py-3 text-white font-semibold transition-colors duration-200 shadow-sm">
                        Coming Soon
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Navbar />
          <Footer />
        </div>
      </div>
    </div>
  );
}

export default Pricing;
